import { useState } from 'react';

import { Button, FormControl, InputLabel, Select, TextField, makeStyles } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { useAdminGetCategories } from '../../../categories/hooks/admin-get-categories.hook';
import { useAdminGetSubCategories } from '../../../categories/hooks/admin-get-sub-categories.hook';
import { Category } from '../../../categories/types/category.type';
import { SubCategory } from '../../../categories/types/sub-category.type';
import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import {
  ENTITY_STATUS_FILTERS,
  ENTITY_STATUS_FILTER_LABELS,
  EntityStatusFilter,
  toEntityListFilters,
} from '../../../shared/constants/entity-status-filter.constants';
import { DEFAULT_ROWS_PER_PAGE } from '../../../shared/constants/table.constants';
import { useAdminGetProducts } from '../../hooks/admin-get-products.hook';
import { useUpdateProduct } from '../../hooks/update-product.hook';
import { useUpdateProductsPositions } from '../../hooks/update-products-positions.hook';
import ProductsTable from '../../components/Tables/ProductsTable.component';
import CreateProductDialog from '../../components/Modals/CreateProductDialog.component';

const NAME_SEARCH_INPUT_ID = 'products-name-search';
const STATUS_FILTER_INPUT_ID = 'products-status-filter';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },

  filters: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem',
  },

  createButton: {
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

interface ActionContainerProps {
  nameSearch: string;
  categoryFilter: Category | null;
  subCategoryFilter: SubCategory | null;
  statusFilter: EntityStatusFilter;
  categories: Category[];
  subCategories: SubCategory[];
  onNameSearchChange: (value: string) => void;
  onCategoryFilterChange: (value: Category | null) => void;
  onSubCategoryFilterChange: (value: SubCategory | null) => void;
  onStatusFilterChange: (value: EntityStatusFilter) => void;
}

function ActionContainer({
  nameSearch,
  categoryFilter,
  subCategoryFilter,
  statusFilter,
  categories,
  subCategories,
  onNameSearchChange,
  onCategoryFilterChange,
  onSubCategoryFilterChange,
  onStatusFilterChange,
}: ActionContainerProps) {
  const classes = useStyles();

  return (
    <section className={classes.filters}>
      <TextField
        id={NAME_SEARCH_INPUT_ID}
        size="small"
        label="Rechercher"
        type="text"
        variant="outlined"
        value={nameSearch}
        onChange={(event) => onNameSearchChange(event.target.value)}
      />
      <Autocomplete
        style={{ minWidth: '200px' }}
        value={categoryFilter}
        options={[...categories].sort((first, second) => first.name.localeCompare(second.name))}
        groupBy={(option) => option.name[0].toUpperCase()}
        getOptionLabel={(option) => option.name}
        getOptionSelected={(option, selected) => option.id === selected.id}
        onChange={(event, selectedOption: Category | null) => onCategoryFilterChange(selectedOption)}
        renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Rubrique" />}
      />
      <Autocomplete
        style={{ minWidth: '200px' }}
        value={subCategoryFilter}
        options={[...subCategories].sort((first, second) => first.name.localeCompare(second.name))}
        groupBy={(option) => option.name[0].toUpperCase()}
        getOptionLabel={(option) => option.name}
        getOptionSelected={(option, selected) => option.id === selected.id}
        onChange={(event, selectedOption: SubCategory | null) => onSubCategoryFilterChange(selectedOption)}
        renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Sous-rubrique" />}
      />
      <FormControl variant="outlined" size="small">
        <InputLabel htmlFor={STATUS_FILTER_INPUT_ID}>Statut</InputLabel>
        <Select
          native
          label="Statut"
          value={statusFilter}
          inputProps={{ id: STATUS_FILTER_INPUT_ID }}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
            onStatusFilterChange(event.target.value as EntityStatusFilter)
          }
        >
          {ENTITY_STATUS_FILTERS.map((status) => (
            <option key={status} value={status}>
              {ENTITY_STATUS_FILTER_LABELS[status]}
            </option>
          ))}
        </Select>
      </FormControl>
    </section>
  );
}

export default function AdminProductsView() {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [nameSearch, setNameSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<Category | null>(null);
  const [subCategoryFilter, setSubCategoryFilter] = useState<SubCategory | null>(null);
  const [statusFilter, setStatusFilter] = useState<EntityStatusFilter>('all');

  const debouncedSearch = useDebounce(nameSearch, 400);

  const { categories } = useAdminGetCategories();
  const { subCategories } = useAdminGetSubCategories({ categoryId: categoryFilter?.id });
  const { isLoading, isRefetching, products, totalCount, refetch } = useAdminGetProducts({
    page: page + 1,
    limit: rowsPerPage,
    sortBy: 'position',
    sortDirection: 'asc',
    filters: {
      name: debouncedSearch || undefined,
      category: categoryFilter?.id || undefined,
      subCategory: subCategoryFilter?.id || undefined,
      ...toEntityListFilters(statusFilter),
    },
  });

  const isDragEnabled =
    Boolean(categoryFilter) && Boolean(subCategoryFilter) && statusFilter === 'all' && !debouncedSearch;
  const { updatePositions } = useUpdateProductsPositions();
  const { updateProduct } = useUpdateProduct();

  const handlePositionsChange = (updatedProducts: { id: string; position: number }[]) => {
    updatePositions(updatedProducts);
  };

  const handleToggleActive = async (productId: string, isActive: boolean): Promise<boolean> => {
    const result = await updateProduct({ productId, productData: { isActive } });
    return result !== null;
  };

  const handleNameSearchChange = (value: string) => {
    setNameSearch(value);
    setPage(0);
  };

  const handleCategoryFilterChange = (value: Category | null) => {
    setCategoryFilter(value);
    setSubCategoryFilter(null);
    setPage(0);
  };

  const handleSubCategoryFilterChange = (value: SubCategory | null) => {
    setSubCategoryFilter(value);
    setPage(0);
  };

  const handleStatusFilterChange = (value: EntityStatusFilter) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <section className={classes.root}>
      <h2>Produits</h2>
      <div className={classes.toolbar}>
        <ActionContainer
          nameSearch={nameSearch}
          categoryFilter={categoryFilter}
          subCategoryFilter={subCategoryFilter}
          statusFilter={statusFilter}
          categories={categories}
          subCategories={subCategories}
          onNameSearchChange={handleNameSearchChange}
          onCategoryFilterChange={handleCategoryFilterChange}
          onSubCategoryFilterChange={handleSubCategoryFilterChange}
          onStatusFilterChange={handleStatusFilterChange}
        />
        <Button size="small" variant="contained" className={classes.createButton} onClick={() => setDialogOpen(true)}>
          Créer un produit
        </Button>
      </div>
      <ProductsTable
        products={products}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        isLoading={isLoading || isRefetching}
        isDragEnabled={isDragEnabled}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onPositionsChange={handlePositionsChange}
        onToggleActive={handleToggleActive}
      />
      <CreateProductDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onProductCreated={refetch} />
    </section>
  );
}
